import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ColumnsTableVehicles from "./components/ColumnsTableVehicles";
import { useTranslation } from "react-i18next";
import {
  deleteVehicleApi,
  getPaginatedVehicleDataApi,
  searchVehiclesApi,
} from "../../../services/customAPI";
import ReactPaginate from "react-paginate";
import "./vehicles.css";
import Loader from "components/loader/loader";
import "react-toastify/dist/ReactToastify.css";
import Card from "components/card";
import { useDisclosure } from "@chakra-ui/hooks";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Button, ChakraProvider } from "@chakra-ui/react";
import imageCar from "../../../assets/images/car.svg";
import deleteIcon from "../../../assets/svg/deleteIcon.svg";
import Navbar from "components/navbar";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "utils/commons";
import {
  PageClickEvent,
  VehicleSearchApiResponse,
  VehicleTableActionDTO,
  VehicleTableRowDTO,
} from "types/api.types";

const Vehicles: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const currentPage = useRef<number>(1);
  const limit = 10;
  const [selectedItem, setSelectedItem] = useState<VehicleTableRowDTO | null>(
    null
  );
  const [pageCount, setPageCount] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [vehicleData, setVehicleData] = useState<VehicleTableRowDTO[]>([]);
  const [pageItemStartNumber, setPageItemStartNumber] = useState<number>(0);
  const [pageItemEndNumber, setPageItemEndNumber] = useState<number>(0);

  const [searchParams, setSearchParams] = useSearchParams();

  const getFilterFromUrl = (): Types.VehicleStatus | string => {
    const data = searchParams.get("status");
    if (data === Types.VehicleStatus.AVAILABLE || data === "available") return Types.VehicleStatus.AVAILABLE;
    if (data === Types.VehicleStatus.UNAVAILABLE || data === "unavailable") return Types.VehicleStatus.UNAVAILABLE;
    return "ALL";
  };

  const [vehicleStatus, setVehicleStatus] = useState<Types.VehicleStatus | string>(getFilterFromUrl());

  const updateUrlParams = useCallback((status: Types.VehicleStatus | string, page: number) => {
    const params = new URLSearchParams(searchParams);
    if (status && status !== "ALL") {
      params.set("status", status);
    } else {
      params.delete("status");
    }
    if (page > 1) {
      params.set("page", String(page));
    } else {
      params.delete("page");
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleStatusChange = useCallback((status: Types.VehicleStatus | string) => {
    setVehicleStatus(status);
    currentPage.current = 1;
    updateUrlParams(status, 1);
  }, [updateUrlParams]);

  const firstRender = useRef(true);
  const { t } = useTranslation();
  const setPageItemRange = (
    currPageNumber: number,
    maxItemRange: number
  ): void => {
    let startNumber = currPageNumber * limit - limit + 1;
    if (startNumber < 0) {
      startNumber = 0;
    }
    setPageItemStartNumber(startNumber);

    const endNumber = currPageNumber * limit;
    setPageItemEndNumber(Math.min(endNumber, maxItemRange));
  };

  const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_URL}/uploads/`;

  const convertToUsableDriverArray = useCallback(
    (vehicleArray: DTO.VehicleDTO[]): VehicleTableRowDTO[] =>
      vehicleArray.map((vehicle) => ({
        vehicleName: {
          name: vehicle.vehicleModel ?? vehicle.vehicleName ?? "",
          path: vehicle.profileImageKey
            ? `${BASE_IMAGE_URL}${vehicle.profileImageKey}`
            : imageCar,
        },
        vehicleNumber: vehicle.vehicleNumber,
        vehicleType: vehicle.vehicleCategory,
        vehicleStatus: vehicle.vehicleStatus ?? Types.VehicleStatus.AVAILABLE,
        action: {
          id: vehicle.id ?? 0,
          documentsKey: vehicle.documentsKeyVehicle,
          profileImageKey: vehicle.profileImageKey,
          approved: vehicle.approved,
        },
        driverId: vehicle.driver?.id,
        driverName: vehicle.driver ? `${vehicle.driver.firstName || ""} ${vehicle.driver.lastName || ""}`.trim() : "",
        transportModes: vehicle.transportModes ?? [],
        primaryTransportMode: vehicle.primaryTransportMode ?? "",
        vehicleTypeName: vehicle.vehicleType ?? "",
      })),
    [BASE_IMAGE_URL]
  );

  const getPaginatedVehicleData = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await getPaginatedVehicleDataApi({
        page: currentPage.current,
        limit: limit,
        status: vehicleStatus !== "ALL" ? vehicleStatus : undefined,
      });

      if (!response.data) {
        errorToast(t("Alguma coisa deu errado!"));
      }

      setPageCount(Math.ceil(response.totalVehicles / limit));
      setVehicleData(convertToUsableDriverArray(response.data.vehicles || []));
      setPageItemRange(currentPage.current, response.totalVehicles);
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao carregar veículos"));
    } finally {
      setIsLoading(false);
    }
  }, [convertToUsableDriverArray, limit, vehicleStatus, t]);

  const searchVehicles = useCallback(
    async (): Promise<VehicleSearchApiResponse | null> => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        const response = await searchVehiclesApi({
          page: currentPage.current,
          limit: limit,
          query: searchText.trim(),
          status: vehicleStatus !== "ALL" ? vehicleStatus : undefined,
        });

        if (!response) {
          errorToast(t("Alguma coisa deu errado!"));
        }
        return response;
      } catch (error) {
        setVehicleData([]);
        setPageCount(1);
        setErrorMessage(t("Erro ao buscar veículos"));
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [limit, searchText, vehicleStatus, t]);

  const searchVehicleFunction = useCallback(async () => {
    try {
      const response = await searchVehicles();
      if (!response) {
        return;
      }
      setPageCount(Math.ceil(response.total / limit));
      setVehicleData(convertToUsableDriverArray(response.data || []));
      setPageItemRange(currentPage.current, response.total);
    } catch (error) {
      errorToast(t("Alguma coisa deu errado!"));
      setErrorMessage(t("Erro ao buscar veículos"));
    } finally {
      setIsLoading(false);
    }
  }, [convertToUsableDriverArray, limit, searchVehicles, t]);

  const deleteHandle = async (data: VehicleTableActionDTO) => {
    setIsLoading(true);
    setErrorMessage("");
    onClose();
    try {
      if (!data.id) {
        errorToast(t("vehicleCantDelete"));
        setIsLoading(false);
        return;
      }
      const response = await deleteVehicleApi(data);
      if (response?.message) {
        successToast(t("Veículo deletado com sucesso!"));
        if (vehicleData.length % limit === 1) {
          currentPage.current = currentPage.current - 1;
        }
        getPaginatedVehicleData();
      } else {
        errorToast(t("vehicleCantDelete"));
      }
      setIsLoading(false);
    } catch (error) {
      errorToast(t("vehicleCantDelete"));
      setErrorMessage(t("Erro ao excluir veículo"));
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = async (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    if (searchText.trim() === "") {
      return;
    }
    currentPage.current = 1;
    searchVehicleFunction();
  };

  function handlePageClick(e: PageClickEvent) {
    currentPage.current = e.selected + 1;
    if (searchText !== "") {
      searchVehicleFunction();
    } else {
      getPaginatedVehicleData();
    }
  }

  const handleClickForDeleteModal = (data: VehicleTableRowDTO) => {
    setIsLoading(true);
    setSelectedItem(data);
    onOpen();
    setIsLoading(false);
  };

  const prevVehicleStatus = useRef(vehicleStatus);

  useEffect(() => {
    const statusChanged = prevVehicleStatus.current !== vehicleStatus;
    prevVehicleStatus.current = vehicleStatus;

    if (firstRender.current) {
      firstRender.current = false;
      getPaginatedVehicleData();
      return;
    }

    // When status changes, always reset page and refetch (even with active search)
    if (statusChanged) {
      currentPage.current = 1;
      updateUrlParams(vehicleStatus, 1);
      if (searchText.trim() !== "") {
        searchVehicleFunction();
      } else {
        getPaginatedVehicleData();
      }
      return;
    }

    // When search text is cleared, refetch all data
    if (searchText.trim() === "") {
      currentPage.current = 1;
      updateUrlParams(vehicleStatus, 1);
      getPaginatedVehicleData();
    }
  }, [getPaginatedVehicleData, searchText, vehicleStatus, updateUrlParams, searchVehicleFunction]);

  return (
    <div>
      <Navbar
        flag={true}
        brandText={t("Vehicles")}
        handleSearch={(e: React.SyntheticEvent<EventTarget>) =>
          handleSearchSubmit(e)
        }
        setSearchText={setSearchText}
      />
      {errorMessage ? (
        <Card extra="w-full mt-4 p-4 h-full">
          <div className="text-sm text-red-400">{errorMessage}</div>
        </Card>
      ) : null}
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <>
            <div className="m-4">
              <ColumnsTableVehicles
                tableData={vehicleData}
                handleClickForDeleteModal={handleClickForDeleteModal}
                statusOptions={[
                  { value: "ALL", label: t("All") },
                  { value: Types.VehicleStatus.AVAILABLE, label: t("Available") },
                  { value: Types.VehicleStatus.UNAVAILABLE, label: t("Unavailable") },
                ]}
                setVehicleStatus={handleStatusChange}
                vehicleStatus={vehicleStatus}
              />
              <div
                className="mx-2 "
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color: "#5E6278",
                }}
              >
                <h5>
                  {pageItemStartNumber} - {pageItemEndNumber}
                </h5>
                <div style={{ marginTop: "1rem" }}>
                  <ReactPaginate
                    breakLabel=" .  .  . "
                    nextLabel=">"
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={3}
                    pageCount={pageCount}
                    previousLabel="<"
                    renderOnZeroPageCount={null}
                    marginPagesDisplayed={2}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="previous-page-btn"
                    previousLinkClassName="page-link"
                    nextClassName="next-page-btn"
                    nextLinkClassName="page-link"
                    activeClassName="active"
                    forcePage={currentPage.current - 1}
                  />
                </div>
              </div>
            </div>
          </>
          {isOpen && (
            <ChakraProvider>
              <Modal
                isCentered={true}
                isOpen={isOpen}
                onClose={onClose}
                size="xs"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader></ModalHeader>
                  <div className="mb-2 flex justify-center">
                    <img src={deleteIcon} alt={t("Excluir")} />
                  </div>
                  <ModalBody className="text-center">
                    {t("Are you sure you want to Delete?")} <br />
                    {`"${selectedItem?.vehicleNumber ?? ""}"`}
                  </ModalBody>
                  <div className="mt-3 flex justify-center">
                    <Button
                      className="cancel-delete-modal-button mx-2"
                      onClick={onClose}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      className="delete-modal-button mx-2"
                      onClick={() => {
                        if (!selectedItem?.action?.id) {
                          return;
                        }
                        deleteHandle(selectedItem.action);
                      }}
                    >
                      {t("Delete")}
                    </Button>
                  </div>
                  <ModalFooter></ModalFooter>
                </ModalContent>
              </Modal>
            </ChakraProvider>
          )}
        </>
      )}
    </div>
  );
};

export default Vehicles;
